import { useMemo, useState } from 'react';
import useSWR from 'swr';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@workspace/ui';

import { MappingAssetAccessSheet } from '@/components/mapping/MappingAssetAccessSheet';
import { MappingFeatureUnavailable, MappingPermissionDenied } from '@/components/mapping/MappingFeatureUnavailable';
import {
  buildSpriteRuntimeBaseUrl,
  formatShareScopeLabel,
  isPortalAssetReadOnly,
  normalizeShareScope
} from '@/lib/mapping-assets';
import {
  deleteSpriteSet,
  listSpriteSetShares,
  listSpriteSets,
  replaceSpriteSetShares,
  updateSpriteSet,
  uploadSpriteSet,
  type ShareScope,
  type SpriteSet,
  type SpriteSetUploadPayload
} from '@/lib/mapping-api';
import { useMappingAccess } from '@/lib/useMappingAccess';

const DEFAULT_PAGE_SIZE = 200;

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export default function MapSpritesPage() {
  const mappingAccess = useMappingAccess();
  const [shareScopeFilter, setShareScopeFilter] = useState<'all' | ShareScope>('all');
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState<SpriteSet | null>(null);
  const [accessTarget, setAccessTarget] = useState<SpriteSet | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadState, setUploadState] = useState<{
    name: string;
    description: string;
    shareScope: ShareScope;
    spriteJson: File | null;
    spritePng: File | null;
    spriteJson2x: File | null;
    spritePng2x: File | null;
    spritePreviewJson: File | null;
    spritePreviewPng: File | null;
    spritePreviewJson2x: File | null;
    spritePreviewPng2x: File | null;
  }>({
    name: '',
    description: '',
    shareScope: 'tenant',
    spriteJson: null,
    spritePng: null,
    spriteJson2x: null,
    spritePng2x: null,
    spritePreviewJson: null,
    spritePreviewPng: null,
    spritePreviewJson2x: null,
    spritePreviewPng2x: null
  });

  const { data, error, isLoading, mutate } = useSWR<SpriteSet[]>(
    mappingAccess.accessEnabled ? ['sprite-sets', shareScopeFilter] : null,
    async () =>
      listSpriteSets({
        limit: DEFAULT_PAGE_SIZE,
        share_scope: shareScopeFilter === 'all' ? undefined : shareScopeFilter
      })
  );

  const filteredSprites = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return data;
    return data.filter(sprite =>
      [sprite.name, sprite.description, sprite.id]
        .filter(Boolean)
        .some(value => value!.toLowerCase().includes(needle))
    );
  }, [data, search]);

  const openUpload = (target?: SpriteSet) => {
    setReplaceTarget(target ?? null);
    setFormError(null);
    setUploadState({
      name: target?.name ?? '',
      description: target?.description ?? '',
      shareScope: normalizeShareScope(target?.share_scope),
      spriteJson: null,
      spritePng: null,
      spriteJson2x: null,
      spritePng2x: null,
      spritePreviewJson: null,
      spritePreviewPng: null,
      spritePreviewJson2x: null,
      spritePreviewPng2x: null
    });
    setSheetOpen(true);
  };

  const handleUpload = async () => {
    setFormError(null);
    if (!uploadState.spriteJson || !uploadState.spritePng || !uploadState.spriteJson2x || !uploadState.spritePng2x) {
      setFormError('Upload all four sprite bundle files before saving.');
      return;
    }

    const name = uploadState.name.trim() || replaceTarget?.name || '';
    if (!name) {
      setFormError('Name is required.');
      return;
    }

    setSaving(true);
    const payload: SpriteSetUploadPayload = {
      name,
      description: uploadState.description.trim() || undefined,
      share_scope: uploadState.shareScope,
      sprite_json: uploadState.spriteJson,
      sprite_png: uploadState.spritePng,
      sprite_json_2x: uploadState.spriteJson2x,
      sprite_png_2x: uploadState.spritePng2x,
      sprite_preview_json: uploadState.spritePreviewJson,
      sprite_preview_png: uploadState.spritePreviewPng,
      sprite_preview_json_2x: uploadState.spritePreviewJson2x,
      sprite_preview_png_2x: uploadState.spritePreviewPng2x,
      sprite_set_id: replaceTarget?.id
    };

    try {
      await uploadSpriteSet(payload);
      await mutate();
      setSheetOpen(false);
      setReplaceTarget(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sprite: SpriteSet) => {
    if (!sprite.id) {
      return;
    }
    if (!window.confirm(`Delete sprite set "${sprite.name ?? sprite.id}"?`)) {
      return;
    }
    try {
      await deleteSpriteSet(sprite.id);
      await mutate();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      setFormError(message);
    }
  };

  const handleCopyRuntime = async (sprite: SpriteSet) => {
    const runtimeUrl = buildSpriteRuntimeBaseUrl(sprite.id);
    if (!runtimeUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(runtimeUrl);
    } catch {
      setFormError('Failed to copy the sprite runtime URL.');
    }
  };

  if (!mappingAccess.mappingFeatureEnabled) {
    return <MappingFeatureUnavailable />;
  }

  if (!mappingAccess.mappingPermissionGranted) {
    return <MappingPermissionDenied />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Map Sprites</h1>
          <p className="text-sm text-muted-foreground">Upload sprite bundles that styles can reference through the workbench.</p>
        </div>
        <Button onClick={() => openUpload()}>Upload sprite set</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sprite Sets</CardTitle>
          <CardDescription>Sprite assets visible to the current tenant context.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search sprite sets" />
            <Select value={shareScopeFilter} onValueChange={value => setShareScopeFilter(value as 'all' | ShareScope)}>
              <SelectTrigger className="sm:w-[180px]">
                <SelectValue placeholder="Scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All scopes</SelectItem>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="shared">Shared</SelectItem>
                <SelectItem value="tenant">Tenant</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formError ? (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {formError}
            </div>
          ) : null}

          {error ? (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {error.message}
            </div>
          ) : null}

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : filteredSprites.length === 0 ? (
            <div className="text-sm text-muted-foreground">No sprite sets found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Runtime</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSprites.map(sprite => {
                  const readOnly = isPortalAssetReadOnly(sprite.tenant_id, mappingAccess.featureTenantId);
                  return (
                    <TableRow key={sprite.id}>
                      <TableCell className="font-medium text-foreground">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span>{sprite.name}</span>
                            {readOnly ? <Badge variant="outline">Read-only</Badge> : null}
                          </div>
                          <div className="text-xs text-muted-foreground">{sprite.id}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {formatShareScopeLabel(sprite.share_scope)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground">
                        {buildSpriteRuntimeBaseUrl(sprite.id) || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(sprite.updated_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleCopyRuntime(sprite)}>
                            Copy runtime
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setAccessTarget(sprite);
                              setAccessOpen(true);
                            }}
                          >
                            Access
                          </Button>
                          {!readOnly ? (
                            <Button variant="outline" size="sm" onClick={() => openUpload(sprite)}>
                              Replace
                            </Button>
                          ) : null}
                          {!readOnly ? (
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(sprite)}>
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>{replaceTarget ? 'Replace sprite set' : 'Upload sprite set'}</SheetTitle>
            <SheetDescription>Provide the standard runtime sprite bundle. A sharper non-SDF preview bundle is optional.</SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sprite-name">Name</Label>
              <Input
                id="sprite-name"
                value={uploadState.name}
                onChange={event => setUploadState(prev => ({ ...prev, name: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sprite-description">Description</Label>
              <Input
                id="sprite-description"
                value={uploadState.description}
                onChange={event => setUploadState(prev => ({ ...prev, description: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sprite-scope">Share scope</Label>
              <Select
                value={uploadState.shareScope}
                onValueChange={value => setUploadState(prev => ({ ...prev, shareScope: value as ShareScope }))}
              >
                <SelectTrigger id="sprite-scope">
                  <SelectValue placeholder="Scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="shared">Shared</SelectItem>
                  <SelectItem value="tenant">Tenant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="sprite-json">sprite.json</Label>
                <Input
                  id="sprite-json"
                  type="file"
                  accept=".json,application/json"
                  onChange={event =>
                    setUploadState(prev => ({ ...prev, spriteJson: event.target.files?.[0] ?? null }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sprite-png">sprite.png</Label>
                <Input
                  id="sprite-png"
                  type="file"
                  accept=".png,image/png"
                  onChange={event =>
                    setUploadState(prev => ({ ...prev, spritePng: event.target.files?.[0] ?? null }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sprite-json-2x">sprite@2x.json</Label>
                <Input
                  id="sprite-json-2x"
                  type="file"
                  accept=".json,application/json"
                  onChange={event =>
                    setUploadState(prev => ({ ...prev, spriteJson2x: event.target.files?.[0] ?? null }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sprite-png-2x">sprite@2x.png</Label>
                <Input
                  id="sprite-png-2x"
                  type="file"
                  accept=".png,image/png"
                  onChange={event =>
                    setUploadState(prev => ({ ...prev, spritePng2x: event.target.files?.[0] ?? null }))
                  }
                />
              </div>
            </div>
            <div className="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-4">
              <div className="space-y-1">
                <div className="text-sm font-semibold text-foreground">Optional preview bundle</div>
                <p className="text-xs text-muted-foreground">
                  Use this for a sharper non-SDF picker and legend preview while keeping the runtime sprite bundle tintable.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="sprite-preview-json">sprite_preview.json</Label>
                  <Input
                    id="sprite-preview-json"
                    type="file"
                    accept=".json,application/json"
                    onChange={event =>
                      setUploadState(prev => ({ ...prev, spritePreviewJson: event.target.files?.[0] ?? null }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sprite-preview-png">sprite_preview.png</Label>
                  <Input
                    id="sprite-preview-png"
                    type="file"
                    accept=".png,image/png"
                    onChange={event =>
                      setUploadState(prev => ({ ...prev, spritePreviewPng: event.target.files?.[0] ?? null }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sprite-preview-json-2x">sprite_preview@2x.json</Label>
                  <Input
                    id="sprite-preview-json-2x"
                    type="file"
                    accept=".json,application/json"
                    onChange={event =>
                      setUploadState(prev => ({ ...prev, spritePreviewJson2x: event.target.files?.[0] ?? null }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sprite-preview-png-2x">sprite_preview@2x.png</Label>
                  <Input
                    id="sprite-preview-png-2x"
                    type="file"
                    accept=".png,image/png"
                    onChange={event =>
                      setUploadState(prev => ({ ...prev, spritePreviewPng2x: event.target.files?.[0] ?? null }))
                    }
                  />
                </div>
              </div>
            </div>
            {formError ? (
              <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {formError}
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSheetOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={saving}>
                {saving ? 'Saving…' : replaceTarget ? 'Replace sprite set' : 'Upload sprite set'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <MappingAssetAccessSheet
        open={accessOpen}
        onOpenChange={setAccessOpen}
        assetTypeLabel="Sprite set"
        asset={accessTarget}
        featureTenantId={mappingAccess.featureTenantId}
        loadShares={assetId => listSpriteSetShares(assetId)}
        saveAccess={async (asset, input) => {
          if (!asset.id) {
            throw new Error('Sprite set ID required');
          }
          await updateSpriteSet(asset.id, { share_scope: input.shareScope });
          await replaceSpriteSetShares(asset.id, input.tenantIds);
          await mutate();
        }}
      />
    </div>
  );
}
